import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { BrandLogo } from "./BrandLogo";
import { HiMenu, HiX } from "react-icons/hi";

const pages = [
  { link: "/", key: "nav.home" },
  { link: "/gallery?tab=canvas", key: "nav.canvas" },
  { link: "/contact#commission", key: "nav.commissions" },
  { link: "/#office-collections", key: "nav.officeCollections" },
  { link: "/gallery", key: "nav.fullGallery" },
  { link: "/about", key: "nav.about" },
] as const;

function isNavActive(pathname: string, search: string, hash: string, link: string) {
  if (link === "/") return pathname === "/";
  if (link === "/gallery?tab=canvas") {
    return pathname === "/gallery" && search.includes("tab=canvas");
  }
  if (link === "/gallery") {
    return pathname === "/gallery" && !search.includes("tab=canvas");
  }
  if (link === "/contact#commission") {
    return pathname === "/contact";
  }
  if (link === "/#office-collections") {
    return pathname === "/" && hash === "#office-collections";
  }
  return pathname.startsWith(link);
}

const Navigation = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-50 border-b border-canvas-200/60 bg-canvas-50/80 backdrop-blur-xl">
      <nav className="section-container flex h-16 items-center justify-between sm:h-20">
        <Link to="/" className="transition-opacity hover:opacity-80">
          <BrandLogo />
        </Link>

        <div className="hidden items-center gap-3 lg:gap-5 xl:flex">
          {pages.map((page) => (
            <Link
              key={page.key}
              to={page.link}
              className={`nav-link !text-[13px] ${
                isNavActive(
                  location.pathname,
                  location.search,
                  location.hash,
                  page.link,
                )
                  ? "nav-link-active"
                  : ""
              }`}
            >
              {t(page.key)}
            </Link>
          ))}
          <LanguageSwitcher />
          <Link to="/contact#commission" className="btn-accent !px-4 !py-2 !text-xs">
            {t("nav.inquire")}
          </Link>
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <LanguageSwitcher />
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full border-0 bg-transparent text-canvas-800 transition-colors hover:bg-canvas-200/60"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? t("common.closeMenu") : t("common.openMenu")}
          >
            {menuOpen ? (
              <HiX className="h-6 w-6" />
            ) : (
              <HiMenu className="h-6 w-6" />
            )}
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="border-t border-canvas-200/60 bg-canvas-50/95 backdrop-blur-xl xl:hidden">
          <div className="section-container flex flex-col gap-1 py-4">
            {pages.map((page) => (
              <Link
                key={page.key}
                to={page.link}
                onClick={() => setMenuOpen(false)}
                className={`rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                  isNavActive(
                    location.pathname,
                    location.search,
                    location.hash,
                    page.link,
                  )
                    ? "bg-canvas-200/60 text-canvas-950"
                    : "text-canvas-700 hover:bg-canvas-100"
                }`}
              >
                {t(page.key)}
              </Link>
            ))}
            <Link
              to="/contact#commission"
              onClick={() => setMenuOpen(false)}
              className="btn-accent mt-2 text-center"
            >
              {t("nav.inquire")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
